import { useLocalization } from "@fluent/react";
import { useAppDispatch, useAppSelector, useDataGridCustomLocaleText } from "../../app/hooks";
import { useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { ComputerizedSystem, loadComputerizedSystems } from "./computerizedSystemsApi";
import { unwrapResult } from "@reduxjs/toolkit";
import produce from "immer"
import { showSuccess } from "../notifications/notificationsSlice";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import { StructureName } from "../device/Structure";
import { Box, Paper, TableContainer } from "@mui/material";
import { LoadingContainer } from "../../app/LoadingContainer";
import { ErrorContainer } from "../../app/ErrorContainer";
import { FlexDiv } from "../../app/Utils";
import { QuickSearchToolbar } from "../../app/QuickSearchToolbar";
import { AddFabButton } from "../../app/components/AddFabButton";
import { CreateComputerizedSystem } from "./dialogs/CreateComputerizedSystem";
import { selectSettings } from "../settings/settingsSlice";
import { GampCategoryChip } from "./components/GampCategoryChip";
import { format } from "date-fns";
import { ObjectValidationStatusChip } from "./components/ObjectValidationStatusChip";
import { GoToButton } from "../../app/components/GoToButton";

export const ComputerizedSystemList = () => {
    const { l10n } = useLocalization()
    const dispatch = useAppDispatch()
    const navigate = useNavigate()
    const [computerizedSystem, setComputerizedSystem] = useState<ComputerizedSystem[]>([]);
    const [isLoading, setIsLoading] = useState<boolean>(false)
    const [isError, setIsError] = useState<boolean>(false)
    const [error, setError] = useState(null);
    const localeText = useDataGridCustomLocaleText();
    const [openCreate, setOpenCreate] = useState(false);
    const { locale } = useAppSelector(selectSettings);
    const lang = locale === "pl" ? "pl" : "en";

    useEffect(() => {
        setIsLoading(true);
        dispatch(loadComputerizedSystems())
            .then(unwrapResult)
            .then(setComputerizedSystem)
            .catch((error) => {
                console.error(error);
                setIsError(true);
                setError(error);
            })
            .finally(() => setIsLoading(false));
    }, [])

    const handleOpen = () => setOpenCreate(true);
    const handleCancel = () => setOpenCreate(false);

    const handleSuccess = (computerizedSystem: ComputerizedSystem) => {
        setComputerizedSystem(prev =>
            produce(prev, draft => {
                const idx = draft.findIndex(o => o.id === computerizedSystem.id);
                if (idx >= 0) {
                    draft[idx] = { ...draft[idx], ...computerizedSystem };
                } else {
                    draft.unshift(computerizedSystem);
                }
            })
        );

        setOpenCreate(false);
        dispatch(showSuccess("saved"));
        navigate(`/computerized-systems/edit/${computerizedSystem.id}`)
    };


    const handleGoToClick = (id) => {
        navigate(`/computerized-systems/edit/${id}`)
    };

    const colums: GridColDef[] = [
        {
            field: "name",
            headerName: l10n.getString("cs-name", null, "Nazwa systemu"),
            flex: 1,
            minWidth: 220,
        },
        {
            field: 'structureId',
            headerName: l10n.getString("structure", null, "_structure"),
            flex: 1,
            minWidth: 200,

            renderCell: (params) => {
                return (
                    <StructureName structureId={params.row.structureId} />
                )
            }
        },
        {
            field: 'supplierName',
            headerName: l10n.getString("cs-producer", null, "_Producent"),
            width: 300
        },
        {
            field: 'createDate',
            width: 150,
            headerName: l10n.getString('process-create-date', null, 'Data utworzenia'),
            valueGetter: (_, row) => {
                return row.createDate ? new Date(row.createDate) : "-";
            },
            valueFormatter: (value) => {
                if (!value) {
                    return "";
                }
                return format(value, 'dd-MM-yyyy');
            }
        },
        {
            field: "validationStatus",
            headerName: l10n.getString("cs-validation-status", null, "Status"),
            minWidth: 160,
            renderCell: (p) => (
                <ObjectValidationStatusChip status={p.value ?? (p.row as any)?.validationStatus} lang={lang} />
            ),
        },
        {
            field: "isGxpRelevant",
            headerName: l10n.getString("cs-gxp", null, "GxP"),
            type: "boolean",
            width: 100,
            align: "center",
            headerAlign: "center",
            sortable: true,
        },
        {
            field: "gampCategory",
            headerName: l10n.getString("cs-gamp-category", null, "GAMP 5"),
            minWidth: 250,
            flex: 0.7,
            sortable: true,
            align: "center",
            headerAlign: "center",
            renderCell: (params) => (
                <GampCategoryChip category={params.value ?? (params.row as any)?.gampCategory} lang={lang} />
            ),
            sortComparator: (v1, v2, p1, p2) => {
                const getLabel = (v: any) => {
                    const n = Number(v);
                    const key = !Number.isNaN(n) ? n : undefined;
                    const dict = (window as any).__gampDict?.[lang] as Record<number, string> | undefined;
                    return dict?.[key!] ?? String(v);
                };
                return getLabel(v1).localeCompare(getLabel(v2));
            },
        },
        {
            field: 'actions', type: 'actions', width: 75,
            renderCell: (params) => {
                return (
                    <Box sx={{ width: '100%', display: 'flex', justifyContent: 'flex-start' }}>
                        <GoToButton onClick={() => handleGoToClick(params.id)} />
                    </Box>
                )
            }
        }
    ];

    if (isLoading) {
        return <>
            <LoadingContainer />
        </>
    }


    if (isError) {
        return <>
            <ErrorContainer error={error} />
        </>
    }

    return (
        <>
            <TableContainer component={Paper}>
                <div style={{ display: 'flex', height: '100%' }}>
                    <div style={{ flexGrow: 1 }}>
                        <DataGrid
                            style={{ minHeight: 100, height: 'calc(100vh - 200px)' }}
                            localeText={localeText}
                            rows={computerizedSystem}
                            columns={colums}
                            slots={{ toolbar: QuickSearchToolbar }}
                            initialState={{
                                sorting: { sortModel: [{ field: 'createDate', sort: 'desc' }] }
                            }}
                            onRowDoubleClick={({ row }) => handleGoToClick(row.id)} />
                    </div>
                </div>
                <FlexDiv>
                    <AddFabButton
                        onClick={handleOpen}
                    />
                </FlexDiv>
            </TableContainer>
            {openCreate && (
                <CreateComputerizedSystem
                    onCancel={handleCancel}
                    onSuccess={handleSuccess}
                />
            )}
        </>
    )
}
